import React, { useState, useEffect } from 'react';
import StoryOverlay from '../story/StoryOverlay';
import PLAYER from '../playerData';
import { RESOURCES } from '../content/common';
import { getTotalLevels } from '../content/levels';
import { ASSETS } from '../assetManifest';

function EndOfContentModal({ onClose }) {
  return (
    <div className="absolute inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900/90 text-white p-6 rounded-lg max-w-lg w-full">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">End of Content</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">✕</button>
        </div>
        <p className="text-center mb-4">
          You've completed all available levels! More content coming soon...
        </p>
        <div className="flex justify-center">
          <button 
            onClick={onClose}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function TasksModal({ map, onClose }) {
  const availableStars = PLAYER.resources.stars;
  const unbuiltBuildings = Object.values(map.buildings).filter(building => !building.built);

  return (
    <div className="absolute inset-0 bg-black/50 z-50 flex items-center justify-center">
      <div className="relative w-[370px] h-[547px]">
        <img 
          src={ASSETS.ui.tasksBackground.path} 
          alt="Tasks Background" 
          className="w-[370px] h-[547px] absolute left-0 top-0"
        />
        
        <div className="w-[197px] h-[43px] absolute left-[92px] top-[11px] text-center text-white text-2xl font-extrabold font-Asap uppercase [text-shadow:_0px_4px_4px_rgb(0_0_0_/_0.25)]">
          AVAILABLE TASKS
        </div>
        
        <img 
          src={ASSETS.ui.closeButton.path} 
          alt="Close" 
          className="w-3.5 h-4 absolute right-[24px] top-[16px] cursor-pointer"
          onClick={onClose}
        />
        
        <div className="w-[296px] h-[414px] absolute left-[33px] top-[105px] overflow-y-auto grid grid-cols-2 gap-[18px]">
          {unbuiltBuildings.map(building => (
            <div key={building.id} className="relative w-[126px] h-[194px]">
              <img 
                src={ASSETS.ui.itemCardBackground.path} 
                alt="Task Background" 
                className="w-[126px] h-[194px] absolute left-0 top-0"
              />
              
              <div className="w-24 h-6 absolute left-[15px] top-[8px] text-center text-white text-[10px] font-extrabold font-Asap uppercase leading-[12px] [text-shadow:_0px_1px_0px_rgb(0_0_0_/_1.00)]">
                {building.name}
              </div>
              
              <div className="w-9 h-4 absolute left-[44px] top-[49px]">
                <img 
                  src={RESOURCES.stars.icon} 
                  alt="Stars" 
                  className="w-4 h-4 absolute left-0 top-0"
                />
                <div className="w-[22px] h-3.5 absolute left-[14px] top-[1px] text-center text-[#b860e8] text-xs font-black font-Asap uppercase [text-shadow:_0px_1px_1px_rgb(0_0_0_/_0.62)]">
                  {building.starCost}
                </div>
              </div>
              
              <div className="w-[72px] h-[72px] absolute left-[27px] top-[74px] flex items-center justify-center text-5xl">
                {building.emoji}
              </div>
              
              <div className="absolute w-[108px] h-[30px] left-[9px] top-[157px]">
                <img 
                  src={ASSETS.ui.buildButton.path} 
                  alt="Build Button" 
                  className="w-[108px] h-[30px] absolute left-0 top-0"
                />
                <div 
                  className={`w-24 h-6 absolute left-[6px] top-[3px] text-center text-white text-sm font-extrabold font-Asap uppercase leading-[12px] [text-shadow:_0px_1px_0px_rgb(0_0_0_/_1.00)] ${
                    availableStars >= building.starCost ? 'cursor-pointer' : 'opacity-50'
                  }`}
                  onClick={() => {
                    if (availableStars >= building.starCost) {
                      if (map.buildBuildingWithStars(building.id)) {
                        onClose();
                      }
                    }
                  }}
                >
                  BUILD
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RewardPopup({ reward, onComplete }) {
  useEffect(() => {
    const timer = setTimeout(() => {
      PLAYER.resources.stars += reward.stars;
      PLAYER.resources.gold += reward.gold;
      onComplete();
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div 
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
        bg-purple-900/90 text-white px-6 py-4 rounded-lg shadow-lg
        animate-[popup_1s_ease-out]"
      style={{
        animation: 'popup 1s ease-out'
      }}
    >
      <style>
        {`
          @keyframes popup {
            0% { transform: translate(-50%, -50%) scale(0); opacity: 0; }
            20% { transform: translate(-50%, -50%) scale(1.2); opacity: 1; }
            40% { transform: translate(-50%, -50%) scale(1); }
            60% { transform: translate(-50%, -50%) scale(1); }
            100% { transform: translate(-50%, -50%) scale(0); opacity: 0; }
          }
        `}
      </style>
      <div className="text-2xl font-bold mb-2">Level Complete!</div>
      <div className="flex items-center gap-2 justify-center">
        <div className="flex items-center">
          <span>+{reward.stars}</span>
          <img src={RESOURCES.stars.icon} alt="Stars" width={24} height={24} />
        </div>
        <div className="flex items-center">
          <span>+{reward.gold}</span>
          <img src={RESOURCES.gold.icon} alt="Gold" width={24} height={24} />
        </div>
      </div>
    </div>
  );
}

function MapUI({ map, app, onStartGame, refreshKey }) {
  const [showTasks, setShowTasks] = useState(false);
  const [currentReward, setCurrentReward] = useState(null);
  const [showEndOfContent, setShowEndOfContent] = useState(false);
  
  useEffect(() => {
    if (PLAYER.rewardQueue.length > 0) {
      setCurrentReward(PLAYER.rewardQueue[0]);
    }
  }, []);

  map.update();

  const hasBuildableTasks = Object.values(map.buildings).some(
    building => !building.built && PLAYER.resources.stars >= building.starCost
  );

  // Calculate next unbeaten level
  const totalLevels = getTotalLevels();
  let nextLevel = 1;
  while (PLAYER.completedLevels.includes(nextLevel) && nextLevel <= totalLevels) {
    nextLevel++;
  }
  
  const allLevelsComplete = nextLevel > totalLevels;
  const currentLevel = allLevelsComplete ? totalLevels : nextLevel;
  
  const handlePlayLevel = () => {
    if (allLevelsComplete) {
      setShowEndOfContent(true);
    } else {
      onStartGame(currentLevel);
    }
  };
  
  return (
    <>
      <div className="absolute bottom-[627px] left-0 w-full flex">
        <div className="w-[119px] h-[51px] absolute left-[60px]" onClick={() => setShowTasks(true)}>
          <img 
            src={ASSETS.ui.buttonBackground.path} 
            alt="Tasks Button" 
            className="w-[119px] h-[51px] absolute left-0 top-0"
          />
          <div className="w-[88px] h-[23px] absolute left-[16px] top-[14px] text-center text-white text-base font-extrabold font-Asap uppercase [text-shadow:_0px_4px_4px_rgb(0_0_0_/_0.25)]">
            TASKS
            {hasBuildableTasks && (
              <div className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs">
                !
              </div>
            )}
          </div>
        </div>
        
        <div className="w-[119px] h-[51px] absolute left-[213px]" onClick={handlePlayLevel}>
          <img 
            src={ASSETS.ui.buttonBackground.path} 
            alt="Next Level Button" 
            className="w-[119px] h-[51px] absolute left-0 top-0"
          />
          <div className="w-[88px] h-[23px] absolute left-[16px] top-[14px] text-center text-white text-base font-extrabold font-Asap uppercase whitespace-nowrap [text-shadow:_0px_4px_4px_rgb(0_0_0_/_0.25)]">
            {allLevelsComplete ? `Level ${currentLevel} ✓` : 'NEXT LEVEL'}
          </div>
        </div>
      </div>
      
      {showTasks && (
        <TasksModal map={map} onClose={() => setShowTasks(false)} />
      )}
      {map.currentStoryBeat && (
        <div className="absolute inset-0">
          <StoryOverlay 
            beat={map.currentStoryBeat} 
            onDismiss={() => map.dismissStoryBeat()} 
          />
        </div>
      )}
      {currentReward && (
        <RewardPopup 
          reward={currentReward}
          onComplete={() => {
            PLAYER.rewardQueue.shift();
            setCurrentReward(null);
          }}
        />
      )}
      {showEndOfContent && (
        <EndOfContentModal onClose={() => setShowEndOfContent(false)} />
      )}
    </>
  );
}

export default MapUI;
